import React, { useState } from 'react';
import { AlertCircle, ArrowRight, CheckCircle, Info, X } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * ActionPreview Component
 *
 * Displays a preview of what will be changed by an AI action before execution.
 * Shows entity details, field-level diffs, and optional reason collection.
 *
 * Props:
 * - actionData: Object containing:
 *   - entity: { name, type, context } - The entity being modified
 *   - changes: Array of { field, oldValue, newValue, highlight }
 *   - confirmationRequired: boolean - Whether user must provide a reason
 *   - reasonOptions: Array of strings - Predefined reason choices
 * - onConfirm: (reason?) => void - Called when user confirms the action
 * - onCancel: () => void - Called when user cancels
 */
const ActionPreview = ({ actionData, onConfirm, onCancel, className }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [showReasonInput, setShowReasonInput] = useState(false);

  if (!actionData) {
    return null;
  }

  const { entity, changes, confirmationRequired, reasonOptions } = actionData;

  const handleConfirm = () => {
    if (confirmationRequired && !selectedReason && !customReason.trim()) {
      return; // Require reason if configured
    }
    const reason = selectedReason === 'custom' ? customReason : selectedReason;
    onConfirm?.(reason);
  };

  const handleReasonSelect = (reason) => {
    setSelectedReason(reason);
    setShowReasonInput(reason === 'custom');
  };

  const isConfirmDisabled = confirmationRequired && !selectedReason && !customReason.trim();

  // Format values for display
  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  return (
    <div className={cn('bg-white rounded-lg border border-gray-200 shadow-sm', className)}>
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 p-2 bg-[#e8f4fa] rounded-lg">
              <Info className="h-5 w-5 text-[#07689f]" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Action Preview
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Review the changes before confirming
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Entity Information */}
      {entity && (
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {entity.type}
                </span>
              </div>
              <div className="text-base font-medium text-gray-900 mt-1">
                {entity.name}
              </div>
              {entity.context && (
                <div className="text-sm text-gray-600 mt-1">
                  {entity.context}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Changes Preview */}
      {changes && changes.length > 0 && (
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">
              Changes ({changes.length})
            </span>
          </div>

          <div className="space-y-2">
            {changes.map((change, index) => (
              <div
                key={index}
                className={cn(
                  'rounded-lg p-3 border',
                  change.highlight
                    ? 'bg-[#e8f4fa] border-[#b8dff5]'
                    : 'bg-gray-50 border-gray-200'
                )}
              >
                <div className="text-xs font-medium text-gray-600 mb-2">
                  {change.field}
                </div>
                <div className="flex items-center gap-2">
                  {/* Old Value */}
                  <div className="flex-1">
                    <div className="text-[10px] text-gray-500 mb-1">Current</div>
                    <div className="text-sm text-gray-700 font-mono bg-white rounded px-2 py-1 border border-gray-200">
                      {formatValue(change.oldValue)}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex-shrink-0 mt-4">
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                  </div>

                  {/* New Value */}
                  <div className="flex-1">
                    <div className="text-[10px] text-gray-500 mb-1">New</div>
                    <div className="text-sm text-[#07689f] font-mono font-medium bg-white rounded px-2 py-1 border border-[#b8dff5]">
                      {formatValue(change.newValue)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reason Collection */}
      {reasonOptions && reasonOptions.length > 0 && (
        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <div className="mb-3">
            <label className="text-sm font-medium text-gray-700">
              Why are you making this change?
              {confirmationRequired && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </label>
            <p className="text-xs text-gray-500 mt-0.5">
              This helps maintain an audit trail for compliance
            </p>
          </div>

          <div className="space-y-2">
            {reasonOptions.map((option, index) => (
              <label
                key={index}
                className={cn(
                  'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                  selectedReason === option
                    ? 'bg-[#e8f4fa] border-[#07689f]'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                )}
              >
                <input
                  type="radio"
                  name="reason"
                  value={option}
                  checked={selectedReason === option}
                  onChange={() => handleReasonSelect(option)}
                  className="mt-0.5 h-4 w-4 text-[#07689f] focus:ring-[#07689f]"
                />
                <span className="text-sm text-gray-700">{option}</span>
              </label>
            ))}

            {/* Custom Reason Option */}
            <label
              className={cn(
                'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                selectedReason === 'custom'
                  ? 'bg-[#e8f4fa] border-[#07689f]'
                  : 'bg-white border-gray-200 hover:border-gray-300'
              )}
            >
              <input
                type="radio"
                name="reason"
                value="custom"
                checked={selectedReason === 'custom'}
                onChange={() => handleReasonSelect('custom')}
                className="mt-0.5 h-4 w-4 text-[#07689f] focus:ring-[#07689f]"
              />
              <div className="flex-1">
                <span className="text-sm text-gray-700">Other (specify)</span>
                {showReasonInput && (
                  <textarea
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Enter your reason..."
                    className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#07689f] focus:border-transparent resize-none"
                    rows={3}
                    autoFocus
                  />
                )}
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          className={cn(
            'px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors inline-flex items-center gap-2',
            isConfirmDisabled
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-[#07689f] hover:bg-[#055a7d]'
          )}
        >
          <CheckCircle className="h-4 w-4" />
          Confirm Changes
        </button>
      </div>
    </div>
  );
};

export default ActionPreview;
